import { computed, inject, Injectable, Injector } from "@angular/core";
import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { ApmService } from "@elastic/apm-rum-angular";
import { Transaction } from "@elastic/apm-rum";
import { toObservable } from "@angular/core/rxjs-interop";
import { filter, firstValueFrom, Observable, of, take, timeout } from "rxjs";

import { CalculationTodayStoreService } from "@bitwarden/web-vault/app/services/store/calculation-today/calculation-today.store.service";

import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { StoreLoaderService } from "@bitwarden/web-vault/app/services/dali/store.loader.service";
import { GlossCreditsAccountService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/gloss-credits-account.service";
import { GlossCreditsTransactionSyncService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/gloss-credits-transaction-sync.service";
import { CreditsService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/credits.service";
import { GuideWizardService } from "@vault/web-vault/app/services/guideWizard/guide-wizard.service";
import { CleanupService } from "./cleanup.service";
import { UserStoreService } from "./store/user/user.store.service";
import { PaymentService } from "./subscription/payment.service";
import { DialogFactory } from "../components/dialog/dialogFactory";
import { GlossSync } from "./syncing/gloss.sync";
import { ReferralApi } from "./DataService/referral/referral.api";
import { GlossNotificationHandlerService } from "./notifications/gloss-notification-handler.service";
import { HelperTrialRemaining } from "../shared/utils/helper-trial-remaining";
import { GlobalService } from "@bitwarden/common/services/global/global.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { PreferenceView } from "@bitwarden/web-vault/app/models/view/preference/preference.view";
import { AutoCreditRedemptionService } from "./subscription/auto-credit-redemption.service";
import { GlossSyncService } from "@bitwarden/web-vault/app/services/syncing/gloss.sync.service";
import { UpgradeNavigationService } from "@bitwarden/web-vault/app/services/subscription/upgrade-navigation.service";

/**
 * Safety ceiling on the calc-readiness wait in {@link Bootstrap.bootstrap}. Without it,
 * a tier-2 init failure or a today-calc cycle that never emits would leave the awaited
 * `isCalculationReady$` pending forever, so the `finally` that clears `isBackgroundLoading`
 * / `silentBootstrap` never runs and the loading skeletons hang for the session (PR #3708). On
 * timeout bootstrap simply proceeds — autoSync then runs concurrently with a still-running calc,
 * which is safe, not a new risk.
 */
const CALC_READY_BOOTSTRAP_TIMEOUT_MS = 30_000;

/**
 * Bounded wait on the calc-readiness signal. Resolves when the calc first reports ready,
 * or after `timeoutMs` if it never does — guaranteeing {@link Bootstrap.bootstrap}'s finally
 * runs and the loading flags clear even on a tier-2 init failure or a never-emitting calc cycle
 * (PR #3708 item 16). Exported so the timeout behaviour can be unit-tested directly, without
 * constructing the (large) bootstrap DI graph.
 */
export function awaitCalcReadyOrTimeout(
  isCalculationReady$: Observable<boolean>,
  timeoutMs: number,
): Promise<boolean> {
  return firstValueFrom(
    isCalculationReady$.pipe(
      filter(Boolean),
      take(1),
      timeout({ first: timeoutMs, with: () => of(true) }),
    ),
  );
}

/**
 * Extracts post-sync bootstrap from `AppComponent` so it can run when the vault is ready.
 */
@Injectable({
  providedIn: "root",
})
export class Bootstrap {
  private injector = inject(Injector);
  private appState = inject(AppStateStoreService);
  private storeLoaderService = inject(StoreLoaderService);
  private guideWizardService = inject(GuideWizardService);
  private apiService = inject(ApiService);
  private apmService = inject(ApmService);
  private upgradeNavigation = inject(UpgradeNavigationService);
  private glossCreditsAccountService = inject(GlossCreditsAccountService);
  private glossCreditsTransactionSync = inject(GlossCreditsTransactionSyncService);
  private creditsService = inject(CreditsService);
  private cleanupService = inject(CleanupService);
  private userStoreService = inject(UserStoreService);
  private paymentService = inject(PaymentService);
  private glossSync = inject(GlossSync);
  private glossSyncService = inject(GlossSyncService);
  private referralApi = inject(ReferralApi);
  private glossNotificationHandler = inject(GlossNotificationHandlerService);
  private autoCreditRedemptionService = inject(AutoCreditRedemptionService);
  private calculationTodayStore = inject(CalculationTodayStoreService);
  private TrialRemainingHelper: HelperTrialRemaining = new HelperTrialRemaining();
  private globalService: GlobalService = inject(GlobalService);
  private i18nService: I18nService = inject(I18nService);
  private logService: LogService = inject(LogService);
  private dialogFactory: DialogFactory;
  private bootstrapInFlight = false;
  private bootstrapRunCount = 0;

  constructor() {
    this.dialogFactory = new DialogFactory();
  }
  isStoreReady = computed<boolean>(() => {
    return this.appState.state().isStoreInitialised;
  });

  async initializeStores(): Promise<void> {
    await Promise.all([this.setApmUserContext(), this.storeLoaderService.initialiseTier1()]);
    void this.storeLoaderService.initialiseTier2();
  }

  async bootstrap(): Promise<void> {
    if (!this.isStoreReady()) {
      return;
    }
    if (this.bootstrapInFlight) {
      return;
    }
    this.bootstrapInFlight = true;
    this.bootstrapRunCount++;
    const runCount = this.bootstrapRunCount;
    const bootstrapTx = this.apmService.apm.isActive()
      ? this.apmService.apm.startTransaction("Bootstrap", "custom", { managed: false })
      : null;
    bootstrapTx?.addLabels({ bootstrap_run: runCount });
    try {
      await this.waitForUserLocationDefined();

      const isFirstEverLogin = this.userStoreService.isFirstEverLogin();
      bootstrapTx?.addLabels({ is_first_login: isFirstEverLogin });

      // Bootstrap always runs silently in the background — no full-screen overlay.
      this.glossSyncService.setSilentBootstrap(true);

      let firstLoginSetupSucceeded = true;
      if (isFirstEverLogin) {
        try {
          const creditsAccount =
            await this.glossCreditsAccountService.ensureGlossCreditsAccountExists();
          firstLoginSetupSucceeded = creditsAccount !== null;

          await this._applyPendingReferralCode();
          await this.glossNotificationHandler.drainPendingReferralCredits();
          await this.glossCreditsTransactionSync.syncGlossCreditsTransactions();
          void this.creditsService.checkAndReactToPurchaseHistory();

          await this.autoCreditRedemptionService.handlePostLogin();

          try {
            await this.paymentService.autoGetFreeTrial();
          } catch (e) {
            // Own catch, not the outer one below: a trial failure (e.g. pollSubStatus
            // timeout on a slow-but-successful Stripe call) must not poison
            // firstLoginSetupSucceeded, or it takes isFirstEverLogin() down with it —
            // isFirstEverLogin() already flipped false when ensureGlossCreditsAccountExists()
            // succeeded above, so the guide wizard/dialog gate below would never
            // self-heal on a later login. Trial failure is reported as its own APM
            // signal instead.
            this._reportNonFatal(
              bootstrapTx,
              "free_trial_activation_failed",
              "Free trial activation failed",
              e,
            );
          }
        } catch (e) {
          // Reachable if a step above this catch (credits account creation/sync,
          // referral code, post-login redemption) throws instead of self-catching.
          // Gates the guide wizard and dialog below so they don't go live before a
          // GLOSS Credits account exists to back their rewards.
          firstLoginSetupSucceeded = false;
          this._reportNonFatal(
            bootstrapTx,
            "first_login_setup_failed",
            "First-login bootstrap step failed",
            e,
          );
        }
      } else {
        try {
          // Wait for the calculation pipeline to finish before autoSync reads transactionStore.
          // isTier2Ready only signals that initialize() was called on the stores — the async
          // enrichment/calculation chain may not have completed yet. isCalculationReady$ fires
          // in the finally block of updateBalanceCalculations, guaranteeing real data is present.
          // Bounded wait: if the calc never signals readiness (tier-2 init failure or a
          // cycle that never emits), fall through after the timeout so the finally still
          // runs and the loading flags clear — see awaitCalcReadyOrTimeout.
          await awaitCalcReadyOrTimeout(
            this.calculationTodayStore.isCalculationReady$,
            CALC_READY_BOOTSTRAP_TIMEOUT_MS,
          );

          // Resolve the authoritative subscription from gloss-api before autoSync reads it:
          // the login JWT can be stale for a bundle granted via credit redemption or a CLI
          // grant, so without this, bootstrapAutoSync's Fiskil/Plaid "Bundle required" gate
          // and the upgrade banners could fire for an already-premium user — handlePostLogin()
          // below (which also applies this) runs too late to prevent that flash (#14137).
          await this.autoCreditRedemptionService.syncActiveSubscriptionFromGlossApi();

          await this.glossSync.bootstrapAutoSync();

          await this._applyPendingReferralCode();
          await this.glossNotificationHandler.drainPendingReferralCredits();
          await this.glossCreditsTransactionSync.syncGlossCreditsTransactions();
          void this.creditsService.checkAndReactToPurchaseHistory();

          await this.autoCreditRedemptionService.handlePostLogin();

          bootstrapTx?.addLabels({ step_reached: "cleanup_start" });
          await this.cleanupService.cleanUpDups(20);
          bootstrapTx?.addLabels({ step_reached: "cleanup_done" });
        } catch (e) {
          // Non-fatal — same reasoning as the first-login catch above: a
          // returning-login sync/cleanup failure must NOT skip the guide
          // wizard self-heal below.
          this._reportNonFatal(
            bootstrapTx,
            "returning_login_setup_failed",
            "Returning-login bootstrap step failed",
            e,
          );
        }
      }

      // Hoisted out of the isFirstEverLogin branch: isFirstEverLogin() flips false
      // permanently once ensureGlossCreditsAccountExists() succeeds above, so a
      // wizardInitialise() failure must self-heal on a later login rather than
      // being skipped forever. wizardInitialise() is idempotent, so running it
      // on every login is safe. Gated on firstLoginSetupSucceeded for a first
      // login: the guide's tasks award credits through the GLOSS Credits
      // account created above, so creating them (or opening the dialog) before
      // that account exists would burn a task's completion with no reward.
      if (!isFirstEverLogin || firstLoginSetupSucceeded) {
        try {
          await this.guideWizardService.wizardInitialise();
        } catch (e) {
          this._reportNonFatal(
            bootstrapTx,
            "guide_wizard_init_failed",
            "Guide wizard initialisation failed",
            e,
          );
        }
      }

      if (isFirstEverLogin && firstLoginSetupSucceeded) {
        try {
          this.dialogFactory.openGuideDialog();
        } catch (e) {
          this._reportNonFatal(null, null, "Failed to open guide dialog", e);
        }
      }

      bootstrapTx?.addLabels({ bootstrap_reached_end: true });
      const activeSubscription = this.userStoreService.subscription.activeSubscription();
      if (activeSubscription !== null) {
        if (
          activeSubscription.trialStartDate &&
          !isNaN(activeSubscription.trialStartDate.getTime())
        ) {
          const trialRemainingDays =
            this.TrialRemainingHelper.getTrialRemaining(activeSubscription);
          if (trialRemainingDays >= 0) {
            this.openMessage(trialRemainingDays, activeSubscription?.withCard);
          }
        }
      }
    } catch (e) {
      this._reportNonFatal(bootstrapTx, "bootstrap_failed", "Bootstrap failed", e);
    } finally {
      this.bootstrapInFlight = false;
      this.glossSyncService.setSilentBootstrap(false);
      this.appState.isBackgroundLoading.set(false);
      window.setTimeout(() => bootstrapTx?.end(), 1);
    }
  }

  /**
   * Logs and reports a non-fatal bootstrap-step failure: log service, APM captureError, and
   * (if given) an APM transaction label. `captureError` self-guards on `isEnabled()` and never
   * throws, so it's safe to call unconditionally regardless of `tx`.
   */
  private _reportNonFatal(
    tx: Transaction | null | undefined,
    label: string | null,
    msg: string,
    e: any,
  ): void {
    this.logService.error(`${msg}: ${e?.message ?? e}`);
    this.apmService.apm.captureError(e instanceof Error ? e : new Error(String(e)));
    if (label) {
      tx?.addLabels({ [label]: true });
    }
  }

  /**
   * Applies a referral code captured at signup. The server queues BOTH the
   * referee's joining bonus and the referrer's award in DynamoDB — nothing is
   * minted client-side, so the amount is no longer forgeable and survives
   * cleared localStorage. The queued credits are drained by
   * `GlossNotificationHandlerService.drainPendingReferralCredits()`.
   */
  private async _applyPendingReferralCode(): Promise<void> {
    try {
      const profile = await this.apiService.getProfile();
      await this.referralApi.applyPending(profile.email);
    } catch (e) {
      this.logService.error(`Failed to apply pending referral code: ${e?.message ?? e}`);
    }
  }

  /** Resolves when preferences exist and `userLocation` is set (or immediately if already set). */
  private async waitForUserLocationDefined(): Promise<void> {
    const preferenceView$ = toObservable(this.userStoreService.preferences.preferenceView, {
      injector: this.injector,
    });
    await firstValueFrom(
      preferenceView$.pipe(
        filter(
          (p): p is PreferenceView =>
            p != null && p.userLocation !== undefined && p.userLocation !== null,
        ),
        take(1),
      ),
    );
  }

  private setApmUserContext(): void {
    this.apiService.getProfile().then((profile) => {
      this.apmService.apm.setUserContext({
        id: profile.id,
        username: profile.name,
        email: profile.email,
      });
    });
  }

  openMessage(trialRemainingDays: number, withCard: boolean) {
    this.globalService.showInfoMessage(
      this.i18nService.t("important"),
      `${this.i18nService.t("freeTrialReminder", trialRemainingDays.toString())} ${withCard ? "" : this.i18nService.t("goToRewards")}`,
      false,
      {
        payload: {
          actionLabel: this.i18nService.t("upgrade"),
          onAction: () => this.upgradeNavigation.goToUpgrade(),
        },
      },
    );
  }
}
